import React, { useMemo, useRef, useState } from 'react';
import { Alert, Button, notification, Radio } from 'antd';
import { observer } from 'mobx-react';
import { ErrorRender, Price } from '@partsbadger/library';
import { QuoteLineItemProps } from '@partsbadger/types';
import dayjs from 'dayjs';
import QuoteStore from '../stores/QuoteStore';
import { getPricingState } from '../utils/pricingState';
import { CalculatingPriceState } from './QuoteLineItem/CalculatingPriceState';
import { PricingFeedback } from './PricingFeedback';

interface Props {
    /**
     * Sibling line items that make up a "multiple quantities" group. Each item is
     * a single-quantity variation (fan-out) so the backend keeps pricing them
     * independently; here we fan them back in as one prices table.
     */
    items: Array<QuoteLineItemProps>;
    /** Optional quantity editor rendered directly beneath the pricing guidance. */
    quantitySelector?: React.ReactNode;
    /** Checkout renders the table beneath its own item summary, without a duplicate heading. */
    showHeader?: boolean;
}

interface PriceRow {
    key: string;
    // Line item + option identity, needed to persist a selection change.
    lineItemId: number;
    optionId: string | null;
    quantity: number;
    lead_time: number | null;
    type?: string;
    unit_price: number | null;
    total_price: number | null;
    needsReview: boolean;
    // Manual-quote row whose price is still being decided (analysis not concluded).
    isCalculating: boolean;
    // Mirrors the card view: the option matching the line item's current
    // `lead_time` (falling back to the first option) is the selected price.
    isSelected: boolean;
    // rowSpan for the Quantity cell so it renders once per quantity group.
    quantityRowSpan: number;
    // rowSpan for the Option cell: merges consecutive same-type rows within a
    // quantity group (e.g. 3 Expedited + 1 Standard) so the "Expedited" /
    // "Standard" label appears once per run instead of once per row —
    // however many options of each type there happen to be (1, 2, 3...).
    typeRowSpan: number;
    // True on the first row of a quantity group, used to draw a clear
    // separator between quantities and to alternate the group background.
    isGroupStart: boolean;
    // 0-based index of the quantity group, used to alternate backgrounds so
    // each quantity's block of rows is visually distinguishable at a glance.
    groupIndex: number;
}

/**
 * Run-length spans for consecutive equal `type`s, so the Option column can
 * merge them into a single cell regardless of how many Expedited/Standard
 * rows are actually present (1, 2, 3, only one type, or a mix of both).
 */
const getTypeRowSpans = (options: Array<{ type?: string }>): number[] => {
    const spans = new Array(options.length).fill(0);
    let i = 0;
    while (i < options.length) {
        let span = 1;
        while (i + span < options.length && options[i + span].type === options[i].type) {
            span++;
        }
        spans[i] = span;
        i += span;
    }
    return spans;
};

export const buildRows = (items: Array<QuoteLineItemProps>): PriceRow[] => {
    // A quantity group should contain one line item per quantity. If an older
    // generated sibling is still present after a quantity update, keep the
    // most recent item so its price options are not rendered twice.
    const itemsByQuantity = new Map<number, QuoteLineItemProps>();
    items.forEach(item => {
        const existing = itemsByQuantity.get(item.quantity);
        if (!existing || item.id > existing.id) {
            itemsByQuantity.set(item.quantity, item);
        }
    });
    const sorted = [...itemsByQuantity.values()].sort((a, b) => a.quantity - b.quantity);
    const rows: PriceRow[] = [];
    // The quantities share the files and the analyses of the part, but their lines are updated one
    // at a time: for a few seconds one is still calculating while another already shows a price or
    // a manual quote. The part is calculating, or it is not: all quantities follow the slowest line.
    const partIsCalculating = sorted.some(li => getPricingState(li) === 'calculating');

    sorted.forEach((li, groupIndex) => {
        // Group Expedited before Standard so the Option column always merges
        // into clean, predictable runs regardless of the order the backend
        // returns them in.
        const options = [...(li?.pricing_options?.prices_by_quantity?.[li.quantity] ?? [])].sort((a, b) => {
            if (a.type === b.type) return 0;
            return a.type === 'Expedited' ? -1 : 1;
        });
        const isSafe = !partIsCalculating && getPricingState(li) === 'priced' && options.length > 0;

        if (isSafe) {
            const selectedOptionId = options.find(option => option.lead_time === li.lead_time)?.id ?? options[0]?.id;
            const typeRowSpans = getTypeRowSpans(options);

            options.forEach((option, i) => {
                rows.push({
                    key: `${li.id}-${option.id ?? i}`,
                    lineItemId: li.id,
                    optionId: option.id ?? null,
                    quantity: li.quantity,
                    lead_time: option.lead_time,
                    type: option.type,
                    unit_price: option.unit_price,
                    total_price: option.total_price,
                    needsReview: false,
                    isCalculating: false,
                    isSelected: option.id === selectedOptionId,
                    quantityRowSpan: i === 0 ? options.length : 0,
                    typeRowSpan: typeRowSpans[i],
                    isGroupStart: i === 0,
                    groupIndex,
                });
            });
        } else {
            rows.push({
                key: `${li.id}-review`,
                lineItemId: li.id,
                optionId: null,
                quantity: li.quantity,
                lead_time: null,
                type: undefined,
                unit_price: null,
                total_price: null,
                needsReview: true,
                isCalculating: partIsCalculating,
                isSelected: false,
                quantityRowSpan: 1,
                typeRowSpan: 1,
                isGroupStart: true,
                groupIndex,
            });
        }
    });

    return rows;
};

/**
 * All quantities in the group share the same material/finish/coating, so the
 * material disclaimer is identical across them — read it off any item that
 * has it (base item is enough).
 */
const getMaterialDisclaimer = (items: Array<QuoteLineItemProps>): string | null => {
    const withDisclaimer = items.find(
        li => li.is_fastlane_safe && li.safe_auto_quote && li.material_disclaimer_message
    );
    return withDisclaimer?.material_disclaimer_message ?? null;
};

/**
 * Each quantity is priced as an independent line item on the backend, so their
 * Expedited options could in theory expire at slightly different times. Scan
 * every quantity's options and surface the soonest expiration so the disclaimer
 * is always accurate for the whole group.
 */
const getExpeditedExpiration = (items: Array<QuoteLineItemProps>): string | null => {
    let soonestTimestamp: number | null = null;

    items.forEach(li => {
        if (!li.is_fastlane_safe) {
            return;
        }
        const options = li?.pricing_options?.prices_by_quantity?.[li.quantity] ?? [];
        options
            .filter(option => option.type === 'Expedited' && option.expiration_date)
            .forEach(option => {
                const timestamp = dayjs(option.expiration_date).valueOf();
                if (!Number.isFinite(timestamp)) {
                    return;
                }

                if (soonestTimestamp === null || timestamp < soonestTimestamp) {
                    soonestTimestamp = timestamp;
                }
            });
    });

    return soonestTimestamp !== null ? dayjs(soonestTimestamp).format('MM-DD-YYYY hh:mm A') + ' (CST)' : null;
};

export const MultipleQuantitiesPrices = observer(({ items, quantitySelector, showHeader = true }: Props) => {
    const rows = buildRows(items);
    const expeditedExpiration = getExpeditedExpiration(items);
    const materialDisclaimer = getMaterialDisclaimer(items);

    // Mirrors buildRows' own de-duplication (keep the most recent item per
    // quantity) so remove/sync actions always target the same record the
    // table actually renders, instead of a stale transient sibling.
    const canonicalItems = useMemo(() => {
        const itemsByQuantity = new Map<number, QuoteLineItemProps>();
        items.forEach(item => {
            const existing = itemsByQuantity.get(item.quantity);
            if (!existing || item.id > existing.id) {
                itemsByQuantity.set(item.quantity, item);
            }
        });
        return [...itemsByQuantity.values()].sort((a, b) => a.quantity - b.quantity);
    }, [items]);

    // Tracks the row being persisted so only that row's radio shows a busy
    // state instead of blocking the whole table.
    const [updatingKey, setUpdatingKey] = useState<string | null>(null);
    // Radio's onChange and the cell's onClick both fire for a single click on
    // the radio (event bubbling) — guard synchronously since the `updatingKey`
    // state hasn't re-rendered yet when the second handler runs.
    const isSubmittingRef = useRef(false);

    const handleSelect = async (row: PriceRow) => {
        if (row.isSelected || row.optionId == null || isSubmittingRef.current) {
            return;
        }

        isSubmittingRef.current = true;
        setUpdatingKey(row.key);

        await QuoteStore.updateItemPrice({
            id: row.lineItemId,
            price_id: row.optionId,
        })
            .catch(error => {
                notification.error({
                    message: 'Error',
                    description: <ErrorRender error={error} />,
                });
            })
            .finally(() => {
                setUpdatingKey(null);
                isSubmittingRef.current = false;
            });
    };

    const quantityGroups = rows.reduce<Map<number, PriceRow[]>>((groups, row) => {
        const group = groups.get(row.quantity) ?? [];
        group.push(row);
        groups.set(row.quantity, group);
        return groups;
    }, new Map());

    const priceTag = (type?: string) => {
        if (!type) {
            return null;
        }
        const isExpedited = type === 'Expedited';
        return (
            <span
                className={`text-white text-xs px-2 py-0.5 rounded-full ${
                    isExpedited ? 'bg-blue-500' : 'bg-green-600'
                }`}
            >
                {type}
            </span>
        );
    };

    return (
        <div
            data-quantities-syncing={QuoteStore.isSyncingQuantities ? 'true' : 'false'}
            data-testid="multiple-quantities-prices"
        >
            {showHeader ? (
                <div className="mb-2">
                    <div className="font-semibold text-gray-700 text-base">Quantities to quote</div>
                    <div className="text-gray-600 font-size-sm">
                        Choose one price and lead time for each quantity you may want to order.
                    </div>
                </div>
            ) : null}
            {quantitySelector ? <div className="mb-2">{quantitySelector}</div> : null}
            <div className="multiple-quantities-prices">
                {[...quantityGroups.entries()].map(([quantity, priceRows]) => {
                    const isCalculating = priceRows[0]?.isCalculating;
                    const needsReview = priceRows[0]?.needsReview && !isCalculating;
                    const lineItem = canonicalItems.find(item => item.quantity === quantity);
                    // Anchor sync on a surviving sibling, not the row being
                    // removed — otherwise reconcile can recreate/mutate the
                    // quantity the user just dropped.
                    const remainingItems = canonicalItems
                        .filter(item => item.quantity !== quantity)
                        .sort((a, b) => a.quantity - b.quantity);
                    const remainingQuantities = remainingItems.map(item => item.quantity);
                    const syncAnchorItem = remainingItems[0] ?? lineItem;
                    return (
                        <section
                            className={needsReview ? 'multiple-quantity-manual-review' : 'multiple-quantity-group'}
                            data-line-item-id={lineItem?.id}
                            data-needs-review={needsReview ? 'true' : 'false'}
                            data-quantity={quantity}
                            data-testid={needsReview ? 'quantity-manual-review' : 'quantity-price-group'}
                            key={quantity}
                        >
                            {isCalculating ? (
                                <div className="multiple-quantity-group-calculating" data-testid="quantity-calculating">
                                    <div className="multiple-quantity-group-title">
                                        {quantity} {quantity === 1 ? 'unit' : 'units'}
                                    </div>
                                    <CalculatingPriceState />
                                </div>
                            ) : needsReview ? (
                                <Alert
                                    action={
                                        <div className="multiple-quantity-manual-review-actions">
                                            {lineItem ? (
                                                <Button
                                                    danger
                                                    data-quantity={quantity}
                                                    data-testid="quantity-price-remove"
                                                    disabled={!remainingQuantities.length}
                                                    onClick={() => {
                                                        if (syncAnchorItem) {
                                                            QuoteStore.syncQuantities(
                                                                syncAnchorItem,
                                                                remainingQuantities
                                                            );
                                                        }
                                                    }}
                                                    size="small"
                                                    type="link"
                                                >
                                                    Remove
                                                </Button>
                                            ) : null}
                                        </div>
                                    }
                                    className="manual-review-required-message manual-quote-required-alert"
                                    data-testid="manual-quote-alert"
                                    description="No instant price is available. Our team will provide pricing and lead time."
                                    message={
                                        <strong>
                                            {quantity} {quantity === 1 ? 'unit' : 'units'} · Manual quote required
                                            <PricingFeedback lineItemId={lineItem?.id} />
                                        </strong>
                                    }
                                    showIcon={false}
                                    style={{ width: '100%', borderRadius: 8 }}
                                    type="info"
                                />
                            ) : (
                                <>
                                    <div className="multiple-quantity-group-title-row">
                                        <div className="multiple-quantity-group-title">
                                            {quantity} {quantity === 1 ? 'unit' : 'units'}
                                        </div>
                                        {lineItem ? (
                                            <Button
                                                danger
                                                data-quantity={quantity}
                                                data-testid="quantity-price-remove"
                                                disabled={!remainingQuantities.length}
                                                onClick={() => {
                                                    if (syncAnchorItem) {
                                                        QuoteStore.syncQuantities(syncAnchorItem, remainingQuantities);
                                                    }
                                                }}
                                                size="small"
                                                type="link"
                                            >
                                                Remove
                                            </Button>
                                        ) : null}
                                    </div>
                                    <div className="multiple-quantity-group-options">
                                        {priceRows.map(row => {
                                            const selectable = updatingKey === null;
                                            return (
                                                <div
                                                    aria-checked={row.isSelected}
                                                    className={`multiple-quantity-price-row ${
                                                        row.isSelected ? 'is-selected' : ''
                                                    }`}
                                                    data-lead-time={row.lead_time ?? undefined}
                                                    data-option-type={row.type}
                                                    data-quantity={quantity}
                                                    data-selected={row.isSelected ? 'true' : 'false'}
                                                    data-testid="quantity-price-option"
                                                    key={row.key}
                                                    onClick={() => handleSelect(row)}
                                                    onKeyDown={event => {
                                                        if (
                                                            selectable &&
                                                            (event.key === 'Enter' || event.key === ' ')
                                                        ) {
                                                            event.preventDefault();
                                                            handleSelect(row);
                                                        }
                                                    }}
                                                    role="radio"
                                                    tabIndex={selectable ? 0 : undefined}
                                                    title="Click to select option"
                                                >
                                                    <div className="multiple-quantity-lead-time">
                                                        <Radio
                                                            checked={row.isSelected}
                                                            disabled={updatingKey !== null}
                                                            onChange={() => handleSelect(row)}
                                                        />
                                                        <div>
                                                            <div className="multiple-quantity-price-label">
                                                                Lead time
                                                            </div>
                                                            <strong>{`${row.lead_time} business days`}</strong>
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <div className="multiple-quantity-price-label">Unit price</div>
                                                        <strong>
                                                            <Price value={row.unit_price ?? 0} /> / unit
                                                        </strong>
                                                    </div>
                                                    <div>
                                                        <div className="multiple-quantity-price-label">Total price</div>
                                                        <strong>
                                                            <Price value={row.total_price ?? 0} />
                                                        </strong>
                                                    </div>
                                                    <div className="multiple-quantity-option-tag">
                                                        {priceTag(row.type)}
                                                        {row.type === 'Standard' &&
                                                        !items.find(li => li.id === row.lineItemId)
                                                            ?.is_fastlane_safe ? (
                                                            <PricingFeedback
                                                                lineItemId={row.lineItemId}
                                                                variant="no-expedite"
                                                            />
                                                        ) : null}
                                                        {row.type === 'Expedited' &&
                                                        priceRows.find(candidate => candidate.type === 'Expedited')
                                                            ?.key === row.key &&
                                                        !items.find(li => li.id === row.lineItemId)
                                                            ?.is_standard_safe ? (
                                                            <PricingFeedback
                                                                lineItemId={row.lineItemId}
                                                                variant="no-standard"
                                                            />
                                                        ) : null}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </section>
                    );
                })}
            </div>

            {expeditedExpiration || materialDisclaimer ? (
                <div className="price-option-disclaimers mt-2 mb-1">
                    {expeditedExpiration ? (
                        <span
                            className="price-option-disclaimer font-light text-gray-700"
                            data-testid="quantity-expedited-expiration"
                        >
                            All Expedited options expire at: <span className="font-bold">{expeditedExpiration}</span>
                        </span>
                    ) : null}
                    {materialDisclaimer ? (
                        <span
                            className="price-option-disclaimer text-gray-600 font-size-sm"
                            data-testid="quantity-material-disclaimer"
                        >
                            {materialDisclaimer}
                        </span>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
});

export default MultipleQuantitiesPrices;
